import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../services/api';
import { Package, Eye, EyeOff } from 'lucide-react';
import { useAppVersion } from '../config/version';
import ForgotPasswordDialog from '../components/ForgotPasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const appVersion = useAppVersion();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });

      const { access_token, user } = response.data;
      onLogin(user, access_token);
      toast.success('Succesvol ingelogd!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #1e3a5f 0%, #0a1628 100%)' }}>
      {/* Decoratieve golvende lijnen - geïnspireerd door visitekaartje */}
      <svg 
        className="absolute inset-0 w-full h-full" 
        viewBox="0 0 1920 1080" 
        preserveAspectRatio="xMidYMid slice"
        style={{ opacity: 0.3 }}
      >
        <defs>
          <linearGradient id="waveGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {/* Eerste golf - bovenaan */}
        <path 
          d="M-100,200 Q300,100 600,200 T1200,180 T1800,220 T2400,200 L2400,0 L-100,0 Z" 
          fill="url(#waveGradient1)"
        />
        {/* Tweede golf - midden */}
        <path 
          d="M-100,400 Q200,300 500,380 T1100,350 T1700,400 T2400,380 L2400,200 Q1800,250 1200,220 T600,250 T-100,230 Z" 
          fill="url(#waveGradient2)"
        />
        {/* Derde golf - onderaan */}
        <path 
          d="M-100,1080 Q400,950 800,1000 T1600,980 T2400,1020 L2400,600 Q1800,650 1200,620 T600,660 T-100,640 Z" 
          fill="url(#waveGradient1)"
        />
        {/* Extra subtiele golf */}
        <path 
          d="M-100,800 Q300,700 700,780 T1400,750 T2100,800 T2400,780" 
          fill="none" 
          stroke="url(#waveGradient2)" 
          strokeWidth="2"
        />
      </svg>
      
      <Card className="w-full max-w-md shadow-2xl relative z-10 bg-white/95 backdrop-blur-sm" data-testid="login-card">
        <CardHeader className="space-y-4">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_company-wardrobe/artifacts/4ciazdpc_Zonder%20titel%20%281080%20x%20600%20px%29.png" 
              alt="WASZ.nl Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <CardDescription className="text-center text-base">Log in om toegang te krijgen tot WaszBase</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" data-testid="email-label">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="naam@voorbeeld.nl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" data-testid="password-label">Wachtwoord</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 text-base font-semibold"
              disabled={loading}
              data-testid="login-button"
              style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)' }}
            >
              {loading ? 'Inloggen...' : 'Inloggen'}
            </Button>
            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                data-testid="forgot-password-link"
              >
                Wachtwoord vergeten?
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      <ForgotPasswordDialog open={showForgot} onClose={() => setShowForgot(false)} mode="employee" />
      
      {/* Versienummer */}
      <div className="absolute bottom-4 left-4 text-white/50 text-xs" data-testid="app-version">
        v{appVersion}
      </div>
    </div>
  );
};

export default Login;